/**
 * Üyelik sayfası: plan kartları, AI kredisi kutusu, kredi yükleme (katalog s. 13, s. 27).
 *
 * Plan fiyatı tek kaynaktan: plan_prices_public() (USD). Kart özellikleri plan
 * limitlerinden (settings.js PLAN_LIMITS, açılışta plan_limits tablosuyla
 * eşitlenir); kartta ayrı yazılmış bir vaat yok. Plan ve kredi aynı ödeme
 * yolundan geçer.
 *
 * Bakiye credit_my_balance() ile okunur; ofis personeli yalnız kendi aylık
 * sınırını görür. Yükleme siparişi SUNUCUDA oluşur (/api/pay/order), ödeme
 * sunucuda capture edilir (/api/pay/capture). Tarayıcı tutar göndermez; kredi,
 * veritabanı siparişin donmuş fiyatını sağlayıcının söylediğiyle karşılaştırınca
 * yatar.
 */

import { defineFeature } from '../../shell/feature.ts';
import { legacy } from '../../shell/bridge.ts';

interface Balance {
  ok: boolean; scope: 'own' | 'firm' | 'member'; role?: string; plan?: string; monthly?: number;
  allowance_left?: number; purchased?: number; available?: number; status?: string; resets_on?: string;
  purchases?: boolean; cap?: number; used?: number; left?: number;
  pool?: { available: number; allowance_left: number; monthly: number } | null;
}
interface Pack { id: string; price_minor: number; currency: string; credits: number }
interface PlanPrice { plan: string; price_minor: number; currency: string; period_days: number }
interface Limit { txn: number | null; docs: number | null; clients: number | null }

let plans: PlanPrice[] = [];
let chosenPlan: string | null = null;

let packs: Pack[] = [];
let showPacks = false;
let chosen: string | null = null;

function el(id: string): HTMLElement | null { return document.getElementById(id); }
function esc(s: unknown): string { return legacy.escapeHtml(String(s ?? '')); }
function n(v: unknown): string { return Number(v ?? 0).toLocaleString(); }

export function priceLabel(minor: number, currency: string): string {
  const zero = ['JPY', 'HUF', 'TWD', 'KRW', 'VND', 'CLP', 'ISK', 'UGX', 'XAF', 'XOF'].includes(currency);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(zero ? minor : minor / 100);
  } catch {
    return `${zero ? minor : (minor / 100).toFixed(2)} ${currency}`;
  }
}

function resets(d?: string): string {
  if (!d) return '';
  const x = new Date(`${d}T00:00:00Z`);
  return x.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

async function render(): Promise<void> {
  const box = el('bil-credits');
  const sb = legacy.sb;
  if (!box || !sb) return;
  const [b, p] = await Promise.all([sb.rpc('credit_my_balance'), sb.rpc('credit_packages_public')]);
  const bal = (b && b.data) as Balance | null;
  packs = Array.isArray(p?.data) ? (p.data as Pack[]) : [];
  if (!bal || !bal.ok) { box.innerHTML = ''; return; }

  let body: string;
  if (bal.scope === 'member') {
    body = `<div style="font-size:22px;font-weight:800;margin:4px 0"><span translate="no">${n(bal.left)} / ${n(bal.cap)}</span>`
      + ` <span style="font-size:12px;font-weight:600;color:var(--t2)">left this month</span></div>`
      + `<div style="font-size:11px;color:var(--t2)">Limit set by your firm · resets <span translate="no">${esc(resets(bal.resets_on))}</span></div>`
      + (bal.pool ? `<div style="font-size:11px;color:var(--t2);margin-top:6px">Firm pool: <b translate="no">${n(bal.pool.available)}</b> credits available</div>` : '');
  } else {
    const hold = bal.status === 'hold'
      ? '<div style="font-size:11px;color:var(--red);margin-top:6px">AI is paused on this account after a payment reversal. Please contact support.</div>' : '';
    body = `<div style="font-size:22px;font-weight:800;margin:4px 0"><span translate="no">${n(bal.allowance_left)} / ${n(bal.monthly)}</span>`
      + ` <span style="font-size:12px;font-weight:600;color:var(--t2)">left this month</span></div>`
      + `<div style="font-size:11px;color:var(--t2)">Resets <span translate="no">${esc(resets(bal.resets_on))}</span>`
      + (Number(bal.purchased) > 0 ? ` · <b translate="no">${n(bal.purchased)}</b> top-up credits` : '') + `</div>` + hold;
  }

  const canTopUp = bal.scope !== 'member' || bal.role === 'manager';
  const topUp = canTopUp && bal.purchases && packs.length
    ? `<button class="btn bg bsm" style="margin-top:10px" onclick="bilTopUp()">+ Top up</button>` : '';
  const list = showPacks && packs.length
    ? `<div style="margin-top:10px">` + packs.map((k) =>
      `<div class="pwo${chosen === k.id ? ' best' : ''}" style="margin-bottom:8px" onclick="bilPick('${esc(k.id)}')">`
      + `<div class="pwot"><span class="pwon" translate="no">${n(k.credits)} credits</span><span class="pwop" translate="no">${esc(priceLabel(k.price_minor, k.currency))}</span></div></div>`).join('')
      + `<div id="bil-pp"></div><div id="bil-err" style="color:var(--red);font-size:12px;text-align:center;min-height:14px;margin-top:6px"></div></div>`
    : '';

  box.innerHTML = `<div class="card" style="margin:12px 0">`
    + `<div style="font-size:13px;font-weight:700">✦ AI credits</div>` + body + topUp + list + `</div>`;
  if (chosen) mountPaypal({ package_id: chosen }, 'bil-pp', 'bil-err');
}

/** Kart satırları limitlerden üretilir; null = sınırsız. */
export function planFeatures(limit: Limit | undefined): string[] {
  if (!limit) return [];
  const count = (v: number | null, one: string, many: string, unlimited: string) =>
    v === null ? unlimited : `${v.toLocaleString()} ${v === 1 ? one : many}`;
  return [
    count(limit.txn, 'transaction', 'transactions', 'Unlimited transactions'),
    count(limit.docs, 'document upload', 'document uploads', 'Unlimited document uploads'),
    count(limit.clients, 'client', 'clients', 'Unlimited clients'),
  ];
}
function limitsOf(plan: string): Limit | undefined {
  return ((globalThis as any).PLAN_LIMITS ?? {})[plan];
}

async function renderPlans(): Promise<void> {
  const box = el('bil-plans');
  const sb = legacy.sb;
  if (!box || !sb) return;
  const r = await sb.rpc('plan_prices_public');
  plans = Array.isArray(r?.data) ? (r.data as PlanPrice[]) : [];
  if (!plans.length) {
    box.innerHTML = '<div style="font-size:12px;color:var(--t2);margin-bottom:10px">Plans are not available right now.</div>';
    return;
  }
  const best = plans.length > 1 ? plans[0].plan : null;
  box.innerHTML = plans.map((p) => {
    const per = p.period_days === 30 ? '/mo' : p.period_days === 365 ? '/yr' : ` / ${p.period_days} days`;
    const name = p.plan.charAt(0).toUpperCase() + p.plan.slice(1);
    return `<div class="pwo${best === p.plan ? ' best' : ''}" style="margin-bottom:10px${chosenPlan === p.plan ? ';outline:2px solid var(--red)' : ''}" onclick="bilPickPlan('${esc(p.plan)}')">`
      + (best === p.plan ? '<div class="pwob">⭐ MOST POPULAR</div>' : '')
      + `<div class="pwot"><span class="pwon">${esc(name)} Plan</span><span class="pwop" translate="no">${esc(priceLabel(p.price_minor, p.currency))}${per}</span></div>`
      + `<ul style="margin:6px 0 0 0;padding:0 0 0 16px;font-size:11px;color:var(--t2);line-height:1.8">`
      + planFeatures(limitsOf(p.plan)).map((f) => `<li>${esc(f)}</li>`).join('') + `</ul></div>`;
  }).join('')
    + `<div id="bil-plan-pp"></div><div id="bil-plan-err" style="color:var(--red);font-size:12px;text-align:center;min-height:14px;margin-top:6px"></div>`
    + (chosenPlan ? '' : '<div style="text-align:center;margin:0 0 8px;padding:8px;background:var(--c2);border-radius:8px;font-size:12px;color:var(--t2)">👆 Select a plan and the PayPal button will appear</div>');
  if (chosenPlan) mountPaypal({ plan: chosenPlan }, 'bil-plan-pp', 'bil-plan-err');
}

function mountPaypal(what: { package_id: string } | { plan: string }, targetId: string, errId: string): void {
  const g = globalThis as any;
  const target = el(targetId);
  const err = el(errId);
  const currency = 'plan' in what
    ? plans.find((p) => p.plan === what.plan)?.currency
    : packs.find((k) => k.id === what.package_id)?.currency;
  if (!target || !currency) return;
  /* PayPal SDK USD ile yükleniyor (settings.js loadPPSDK); başka para birimi o SDK'yla ödenemez. */
  if (currency !== 'USD') { if (err) err.textContent = 'This item cannot be paid with PayPal yet.'; return; }
  const clientId = String(g.G?.ppClientId ?? '').trim();
  if (!clientId || typeof g.loadPPSDK !== 'function') { if (err) err.textContent = 'Payment system is not configured.'; return; }
  target.innerHTML = '<div style="text-align:center;padding:10px;color:var(--t2);font-size:12px">Loading PayPal…</div>';
  g.loadPPSDK(clientId, () => {
    if (!g.paypal) { target.innerHTML = ''; if (err) err.textContent = 'PayPal could not be loaded.'; return; }
    target.innerHTML = '';
    let orderId = '';
    g.paypal.Buttons({
      style: { layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay' },
      createOrder: async () => {
        const r = await legacy.postApi('/api/pay/order', what);
        const d = await r.json().catch(() => ({}));
        if (!r.ok || !d.paypal_order_id) throw new Error(d.error || 'Payment could not be started.');
        orderId = d.order_id;
        return d.paypal_order_id;
      },
      onApprove: async () => {
        target.innerHTML = '<div style="text-align:center;padding:12px;color:var(--grn);font-size:13px;font-weight:600">⏳ Verifying…</div>';
        const r = await legacy.postApi('/api/pay/capture', { order_id: orderId });
        const d = await r.json().catch(() => ({}));
        if (r.ok && d.ok) {
          if ('plan' in what) {
            chosenPlan = null;
            await g.sbLoadSubscription?.();
            g.chkSub?.();
            legacy.toast('Your plan is active.');
            g.rMembership?.();
            return;
          }
          legacy.toast('Credits added.');
          chosen = null; showPacks = false;
        } else if (r.status === 202) {
          legacy.toast('Payment is pending. Credits are added as soon as PayPal confirms it.');
        } else {
          legacy.toast(d.error || 'Payment could not be verified.');
        }
        if ('plan' in what) void renderPlans(); else void render();
      },
      onCancel: () => { if (err) err.textContent = 'Payment cancelled.'; },
      onError: (e: unknown) => { if (err) err.textContent = (e as Error)?.message || 'Payment error.'; },
    }).render('#' + targetId);
  }, targetId);
}

/* plan_limits: -1 = sınırsız → istemcide null. Tablo okunamazsa settings.js'teki değerler kalır.
   documents/transactions sütunları HENÜZ YOK (migration 12 bekliyor): adlarını tek tek istemek
   her açılışta 400 döndürüyordu ve tablo hiç okunmuyordu. Artık tüm satır okunur; eksik sütun
   settings.js'teki değerinde bırakılır (undefined'ı null yapmak "sınırsız" demek olurdu),
   sütunlar eklendiğinde kod değişmeden devreye girer. */
export function mergeLimits(
  target: Record<string, Limit>,
  rows: Array<Record<string, unknown>>,
): Record<string, Limit> {
  const v = (x: unknown) => (x === -1 || x === null ? null : Number(x));
  /* Sütun yoksa varsayılan kalır. Planın hiç varsayılanı yoksa (tabloda olup
     settings.js'te olmayan 'basic') FREE değerleri alınır: canlı tetikleyiciler
     de bilinmeyen planı free gibi sınırlıyor (1 müşteri / 2 belge / 50 işlem).
     Boş bırakmak "sınırsız" demek olurdu. */
  const fallback = (target.free ?? {}) as Partial<Limit>;
  const pick = (given: unknown, cur: number | null | undefined, def: number | null | undefined) =>
    given === undefined ? (cur !== undefined ? cur : def ?? null) : v(given);
  for (const row of rows) {
    const plan = typeof row.plan === 'string' ? row.plan : null;
    if (!plan) continue;
    const cur = (target[plan] ?? {}) as Partial<Limit>;
    target[plan] = {
      txn: pick(row.transactions, cur.txn, fallback.txn),
      docs: pick(row.documents, cur.docs, fallback.docs),
      clients: pick(row.clients, cur.clients, fallback.clients),
    };
  }
  return target;
}

async function syncLimits(): Promise<void> {
  for (let i = 0; i < 20 && !legacy.sb; i++) await new Promise((r) => setTimeout(r, 500));
  const sb = legacy.sb;
  const target = (globalThis as any).PLAN_LIMITS;
  if (!sb || !target) return;
  const r = await sb.from('plan_limits').select('*');
  if (!r || r.error || !Array.isArray(r.data)) return;
  mergeLimits(target, r.data as Array<Record<string, unknown>>);
}

export default defineFeature({
  id: 'billing',
  label: 'AI kredisi ve kredi yükleme',
  view: null,
  /* Kart ve paywall limitleri tek kaynaktan: plan_limits (okuma herkese açık). */
  boot: () => { void syncLimits(); },
  legacyGlobals: {
    bilRenderPlans: () => { void renderPlans(); },
    bilPickPlan: (plan: unknown) => { chosenPlan = String(plan); void renderPlans(); },
    bilPlanFeatures: (plan: unknown) => planFeatures(limitsOf(String(plan))),
    bilRenderCredits: () => { void render(); },
    bilTopUp: () => { showPacks = !showPacks; chosen = null; void render(); },
    bilPick: (id: unknown) => { chosen = String(id); void render(); },
  },
});
